/**
 * Pure OHLC merge/dedup helpers (extracted from useChartStore3 so the splice
 * logic used by the visibility-resync backfill — proposal 65 — is unit-testable
 * without loading the whole store/worker/indexeddb graph).
 *
 * The invariant every caller relies on: the returned series is ascending by
 * timestamp with NO duplicate timestamps. Incoming bars win on a tie (they are
 * the freshest server truth), which is exactly what the tail-backfill needs —
 * its requested range overlaps the live tail, so an inexact dedup would double
 * a bar.
 */

import type { OHLCData } from "../types";

/** Collapses duplicate timestamps in an ascending-sorted array, keeping the last occurrence. */
export function dedupeSortedAscending(sorted: OHLCData[]): OHLCData[] {
  const out: OHLCData[] = [];
  for (const bar of sorted) {
    if (out.length > 0 && out[out.length - 1].timestamp === bar.timestamp) {
      out[out.length - 1] = bar;
    } else {
      out.push(bar);
    }
  }
  return out;
}

/**
 * Merge `incoming` bars into `existing`, deduped and sorted by timestamp.
 * Incoming wins on duplicate timestamps. This is the exact splice the
 * visibility-resync backfill leans on: the backfill range [lastGoodBarTs, now]
 * overlaps the live tail, so the overlap bars must dedup instead of doubling.
 */
export function mergeDedupeSorted(
  existing: OHLCData[],
  incoming: OHLCData[],
): OHLCData[] {
  if (incoming.length === 0) return existing;
  const sortedIncoming = dedupeSortedAscending(
    [...incoming].sort((a, b) => a.timestamp - b.timestamp),
  );
  if (existing.length === 0) return sortedIncoming;
  // Fast path: whole batch is strictly newer than the existing series.
  if (sortedIncoming[0].timestamp > existing[existing.length - 1].timestamp) {
    return [...existing, ...sortedIncoming];
  }
  // Linear merge of two sorted runs; incoming wins on duplicate timestamps
  // (same contract as the previous Map-based implementation).
  const out: OHLCData[] = [];
  let i = 0;
  let j = 0;
  while (i < existing.length && j < sortedIncoming.length) {
    const a = existing[i];
    const b = sortedIncoming[j];
    if (a.timestamp < b.timestamp) {
      out.push(a);
      i++;
    } else if (a.timestamp > b.timestamp) {
      out.push(b);
      j++;
    } else {
      out.push(b);
      i++;
      j++;
    }
  }
  while (i < existing.length) out.push(existing[i++]);
  while (j < sortedIncoming.length) out.push(sortedIncoming[j++]);
  return out;
}
